
/**
 * Capacitor plugin provides mechanisms for downloading and uploading files.
 * Reference docs used to derive types & behavior:
 *   https://www.npmjs.com/package/@capacitor/file-transfer
 */

import { mobilePlugins } from "../mobileHelper";
import moment from "moment";
import * as datadog from "@/shared/datadog";

export enum Directory {
  /**
   * The Documents directory.
   * On iOS it's the app's documents directory.
   * Use this directory to store user-generated content.
   * On Android it's the Public Documents folder, so it's accessible from other apps.
   * It's not accessible on Android 10 unless the app enables legacy External Storage
   * by adding `android:requestLegacyExternalStorage="true"` in the `application` tag
   * in the `AndroidManifest.xml`.
   * On Android 11 or newer the app can only access the files/folders the app created.
   *
   * @since 1.0.0
   */
  Documents = "DOCUMENTS",

  /**
   * The Data directory.
   * On iOS it will use the Documents directory.
   * On Android it's the directory holding application files.
   * Files will be deleted when the application is uninstalled.
   *
   * @since 1.0.0
   */
  Data = "DATA",

  /**
   * The Library directory.
   * On iOS it will use the Library directory.
   * On Android it's the directory holding application files.
   * Files will be deleted when the application is uninstalled.
   *
   * @since 1.1.0
   */
  Library = "LIBRARY",

  /**
   * The Cache directory.
   * Can be deleted in cases of low memory, so use this directory to write app-specific files.
   * that your app can re-create easily.
   *
   * @since 1.0.0
   */
  Cache = "CACHE",

  /**
   * The external directory.
   * On iOS it will use the Documents directory.
   * On Android it's the directory on the primary shared/external
   * storage device where the application can place persistent files it owns.
   * These files are internal to the applications, and not typically visible
   * to the user as media.
   * Files will be deleted when the application is uninstalled.
   *
   * @since 1.0.0
   */
  External = "EXTERNAL",

  /**
   * The external storage directory.
   * On iOS it will use the Documents directory.
   * On Android it's the primary shared/external storage directory.
   * It's not accessible on Android 10 unless the app enables legacy External Storage
   * by adding `android:requestLegacyExternalStorage="true"` in the `application` tag
   * in the `AndroidManifest.xml`.
   * It's not accessible on Android 11 or newer.
   *
   * @since 1.0.0
   */

  ExternalStorage = "EXTERNAL_STORAGE",
  /**
   * The external cache directory.
   * On iOS it will use the Documents directory.
   * On Android it's the primary shared/external cache.
   *
   * @since 7.1.0
   */
  ExternalCache = "EXTERNAL_CACHE",

  /**
   * The Library directory without cloud backup. Used in iOS.
   * On Android it's the directory holding application files.
   *
   * @since 7.1.0
   */
  LibraryNoCloud = "LIBRARY_NO_CLOUD",

  /**
   * A temporary directory for iOS.
   * On Android it's the directory holding the application cache.
   *
   * @since 7.1.0
   */
  Temporary = "TEMPORARY",
}

/** Http headers shape used by options. */
export interface HttpHeaders {
  [key: string]: string;
}

/** URL query params shape used by options. */
export interface HttpParams {
  [key: string]: string | number | boolean;
}

/** Progress event payload. */
export interface ProgressStatus {
  /** The type of transfer operation (download or upload). */
  type: "download" | "upload";
  /** The url associated with the transfer. */
  url: string;
  /** Bytes transferred so far. */
  bytes: number;
  /** Total number of bytes if known. */
  contentLength: number;
  /** Whether contentLength is known. */
  lengthComputable: boolean;
}

/** Download options (subset + documented fields). */
export interface DownloadFileOptions {
  /** The URL to download the file from. */
  url: string;
  /**
   * Full destination file path (native: file URI).
   * Use @capacitor/filesystem.getUri() to build a full path.
   */
  path: string;
  /** Emit progress events? (native throttled every ~100ms) */
  progress?: boolean;
  /** HTTP method (default: GET) */
  method?: string;
  /** Query string parameters */
  params?: HttpParams;
  /** Request headers (e.g., Authorization) */
  headers?: HttpHeaders;
  /** Read timeout (ms). Not supported on web. */
  readTimeout?: number;
  /** Connect timeout (ms). iOS conflates with read. Not supported on web. */
  connectTimeout?: number;
  /** Disable automatic redirects. Not supported on web. */
  disableRedirects?: boolean;
  /**
   * Whether to URL-encode params automatically (default: true).
   * Not supported on web.
   */
  shouldEncodeUrlParams?: boolean;
}

/** Download result payload. */
export interface DownloadFileResult {
  path: string;
}

export interface PluginListenerHandle {
  remove: () => Promise<void> | void;
}

/**
 * Downloads a file in the native device
 * @param args.url - string: a full url of a resource to download.
 * @param args.directory(optional) - Directory: Native file directory where the downloaded file is stored.  Defaults to 'Directory.Documents'.
 * @param args.trackProgress(optional) - boolean: Notify users when the file is downloaded.  Defaults to 'true'.
 * @returns {Promise<DownloadFileResult>} A promise that resolves with the DownloadFileResult with the path.
 */
export async function downloadFile (args: {url: string, directory?: Directory, trackProgress?: boolean }): Promise<DownloadFileResult> {
  const directory = args.directory ?? Directory.Documents;
  const trackProgress = args.trackProgress ?? true;
  let fileName = `file_downloaded_${moment().format("MMDDYYYY")}`;

  try {
    const parsedUrl = new URL(args.url);
    const pathname = parsedUrl.pathname;
    fileName = pathname.split("/").pop();
  } catch (err) {
    datadog.log("Mobile download url parsing error.", err, "warn");
  }

  const fileInfo = await mobilePlugins.Filesystem.getUri({
    directory,
    path: `${fileName}`
  });

  const options: DownloadFileOptions = {
    url: args.url,
    path: fileInfo.uri,
    progress: trackProgress
  };

  return downloadWithOptions(options);
}

/**
 * Uses the native FileTransfer plugin.
 * Progress events are global; we filter by (type === "download' && url === options.url).
 */
export async function downloadWithOptions (
  options: DownloadFileOptions
): Promise<DownloadFileResult> {
  let handle: PluginListenerHandle | null = null;
  try {
    if (options.progress) {
      handle = await mobilePlugins.FileTransfer.addListener(
        "progress",
        runOnce(async (evt: ProgressStatus) => {
          if (evt?.type === "download" && evt?.url === options.url) {
            await mobilePlugins.Toast.show({ text: `File downloaded successfully.`, duration: "long", position: "bottom"});
          }
        })
      );
    }

    await mobilePlugins.FileTransfer.downloadFile(options);
    return { path: String(options.path) };
  } catch (err) {
    datadog.log(`File download failed. url:${options.url}`, err, "error");
    throw err;
  } finally {
    // clean up progress listener
    if (handle?.remove) {
      try {
        await handle.remove();
      } catch {
        /* ignore */
      }
    }
  }
}

function runOnce (fn) {
  let hasRun = false;
  return (...args) => {
    if (!hasRun) {
      hasRun = true;
      fn(...args);
    }
  };
}